
import { defineComponent } from 'vue';

import numbro from '@/initNumbro';
import ResultTable from '@/precision-farming/application-maps/components/resultTable/ResultTable.vue';
import { ColumnSettings, TableData } from '@/precision-farming/application-maps/components/resultTable/types';
import weightedAverage from '@/shared/modules/weightedAverage';
import { RootState } from '@/store/types';

import { roundToPrecision } from '../../store/common';
import { Getters } from '../../store/getters';
import { SeedingZone } from '../../store/types';

export default defineComponent({
  name: 'ZonesTableContainer',
  components: { ResultTable },
  data() {
    return {};
  },
  computed: {
    zones(): SeedingZone[] {
      return this.$store.getters['precisionFarming/applicationMaps/seedingSoy/zones'];
    },
    calculation() {
      return (this.$store.state as RootState).precisionFarming.applicationMaps.seedingSoy.calculation;
    },
    varieties() {
      return (this.$store.state as RootState).precisionFarming.applicationMaps.seedingSoy.varieties;
    },
    isDrillVariety(): boolean {
      const variety = this.varieties.current.find((v) => v.id === this.calculation.variety.id);
      return this.calculation.variety.id !== null && variety?.seedingMethod === 'Drill';
    },
    columns(): ColumnSettings[] {
      const baseColumns: ColumnSettings[] = [
        {
          key: 'zone',
          header: {
            title: () => this.$t('Zone') || 'Zone',
          },
          type: 'badge',
        },
        {
          key: 'seedRateGrains',
          header: {
            title: () => this.$t('Ko/m^2') || 'K\u00f6/m\u00B2',
          },
          type: 'numberInput',
          align: 'right',
        },
      ];

      if (this.isDrillVariety) {
        baseColumns.push({
          key: 'seedRateKg',
          header: {
            title: () => this.$t('kg/ha') || 'kg/ha',
          },
          type: 'text',
          align: 'right',
        });
      }

      return baseColumns;
    },
    weightedAvgSeedRate(): number {
      if (!this.zones || this.zones.length === 0) return 0;
      const seedRates = this.zones.map(
        (zone: SeedingZone) => zone.manualSeedRate ?? zone.seedRateWithLossCompensation ?? 0,
      );
      const zoneSizes = this.zones.map((zone: SeedingZone) => zone.size);
      return roundToPrecision(weightedAverage(seedRates, zoneSizes));
    },
    tableData(): TableData[] {
      if (!this.zones || this.zones.length === 0) return [];
      return this.zones.map((zone: SeedingZone) => {
        // display 0 if zone size is 0
        const seedRateValue = zone.size === 0 ? 0 : zone.manualSeedRate ?? zone.seedRateWithLossCompensation ?? 0;
        // Convert Kö/ha to Kö/m²
        const seedRateGrainsPerSqM = seedRateValue / 10000;
        // Calculate kg/ha: Kö/m² * thousand-grain weight / 100
        const seedRateKgPerHa = this.isDrillVariety
          ? Math.round((seedRateGrainsPerSqM * this.calculation.thousandGrainWeight) / 100)
          : 0;

        const rowData: any = {
          zone: {
            name: '',
            color: zone.color,
          },
          seedRateGrains: { value: Math.round(seedRateGrainsPerSqM) },
        };

        if (this.isDrillVariety) {
          rowData.seedRateKg = numbro(seedRateKgPerHa).format({ mantissa: 0 });
        }

        return rowData;
      });
    },
    weightedAvgSeedRateKgPerHa(): number {
      if (!this.isDrillVariety || !this.zones || this.zones.length === 0) return 0;
      const seedRateKgValues = this.zones.map((zone: SeedingZone) => {
        const seedRateValue = zone.manualSeedRate ?? zone.seedRateWithLossCompensation ?? 0;
        const seedRateGrainsPerSqM = seedRateValue / 10000;
        return (seedRateGrainsPerSqM * this.calculation.thousandGrainWeight) / 100;
      });
      const zoneSizes = this.zones.map((zone: SeedingZone) => zone.size);
      return Math.round(weightedAverage(seedRateKgValues, zoneSizes));
    },
    footer(): TableData {
      const footerData: any = {
        zone: this.$t('Durchschnitt') || 'Durchschnitt',
        seedRateGrains: numbro(this.weightedAvgSeedRate / 10000).format({ mantissa: 0 }),
      };

      if (this.isDrillVariety) {
        footerData.seedRateKg = numbro(this.weightedAvgSeedRateKgPerHa).format({ mantissa: 0 });
      }

      return footerData;
    },
  },
  watch: {
    weightedAvgSeedRate() {
      this.$store.dispatch(`precisionFarming/applicationMaps/seedingSoy/setAverageDosage`, this.weightedAvgSeedRate);
    },
  },
  methods: {
    onInput({ key, rowIndex, value }: { key: string; rowIndex: number; value: number | undefined }) {
      if (key !== 'seedRateGrains') return;

      const zoneName = (this.zones as Getters['zones'])[rowIndex]?.name;
      // Convert Kö/m² back to Kö/ha for storage
      const seedRateHa = (value ?? 0) * 10000;
      this.setManualSeedRate(zoneName, seedRateHa);
    },
    setManualSeedRate(zoneName: string, manualSeedRate: number) {
      this.$store.dispatch('precisionFarming/applicationMaps/seedingSoy/setManualSeedRate', {
        zoneName,
        manualSeedRate,
      });
    },
  },
});
